import { Link } from 'react-router-dom';
import { Fill, LegalPage } from './LegalPage.jsx';

/**
 * /terms — Terms of Service.
 *
 * TEMPLATE. Drafted for a subscription web app selling AI-generated sports
 * forecasts for entertainment, paid in cryptocurrency, with email one-time-code
 * accounts and a likely EU user base. Needs review by a qualified lawyer before
 * launch, and every <Fill> below needs a real value.
 */
export function Terms() {
  return (
    <LegalPage title="Terms of Service" updated="15 August 2026">
      <p>
        These Terms of Service (the &ldquo;Terms&rdquo;) govern your use of Don
        Quack, a website and web application operated by{' '}
        <Fill>[Legal entity name]</Fill>, registered at{' '}
        <Fill>[Registered address]</Fill>, company number{' '}
        <Fill>[Company number]</Fill> (&ldquo;Don Quack&rdquo;,
        &ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using the
        service you agree to these Terms. If you do not agree, do not use the
        service.
      </p>

      <h2>1. What Don Quack is — and what it is not</h2>
      <p>
        Don Quack produces <strong>AI-generated probability forecasts</strong>{' '}
        for sports fixtures. We read publicly available bookmaker odds and other
        data, process them with statistical and machine-learning models, and
        present an estimated win probability, a predicted winner, a confidence
        rating, and supporting commentary.
      </p>
      <p>
        <strong>
          The service is provided for entertainment and informational purposes
          only.
        </strong>{' '}
        Nothing on Don Quack is betting advice, gambling advice, investment
        advice, or financial advice of any kind. We do not accept wagers, place
        bets, operate any form of gambling, or handle stakes or winnings.
      </p>
      <p>
        Forecasts are probabilistic estimates about uncertain future events.
        They are frequently wrong. A stated probability is not a promise, a
        guarantee, or a prediction of a certain outcome, and past accuracy does
        not indicate future accuracy. Any decision you make on the basis of a
        forecast — including any decision to gamble — is yours alone, taken at
        your own risk and expense.
      </p>
      <p>
        If gambling is causing you or someone you know harm, support is
        available. In the EU and UK see{' '}
        <Fill>[Local support service and URL]</Fill>.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old to use Don Quack, and old enough to
        form a binding contract where you live. The service is not directed at
        children, and we do not knowingly allow anyone under 18 to hold an
        account. You are responsible for ensuring that using a sports-forecast
        service is lawful in your jurisdiction.
      </p>

      <h2>3. Accounts and sign-in</h2>
      <p>
        Accounts are created with an email address. We send a six-digit
        one-time code to that address to sign you in; there is no password. This
        means <strong>anyone with access to your email inbox can access your
        Don Quack account</strong>, so keep your inbox secure.
      </p>
      <p>
        You agree to provide an email address you control, to keep your account
        details accurate, and not to share your account or one-time codes with
        anyone. Tell us promptly at <Fill>[support email]</Fill> if you believe
        your account has been accessed without your permission.
      </p>

      <h2>4. Plans, pricing and payment</h2>
      <p>
        Don Quack offers a free tier with limited forecasts and one or more paid
        subscription tiers. The features and price of each tier are shown on the
        pricing page at the time you subscribe, and that displayed price is the
        one that applies to your purchase.
      </p>
      <p>
        <strong>Payment is made in cryptocurrency</strong>, processed by our
        payment provider NOWPayments. We do not collect or store your card
        details, wallet keys, or any similar payment credentials. When you start
        a checkout you are handed to NOWPayments, and their own terms and
        privacy policy govern that part of the transaction.
      </p>
      <p>Two consequences of crypto payment you should understand before paying:</p>
      <ul>
        <li>
          <strong>Cryptocurrency transactions are irreversible.</strong> Once a
          payment is confirmed on the relevant blockchain, neither we nor
          NOWPayments can reverse it. There is no chargeback mechanism.
        </li>
        <li>
          <strong>Amounts are converted at the rate quoted at checkout.</strong>{' '}
          Network fees, exchange-rate movement between quote and confirmation,
          or an underpayment may mean your payment does not cover the
          subscription price, in which case the subscription may not activate
          until the balance is settled.
        </li>
      </ul>
      <p>
        Subscriptions run for the period shown at checkout. We will tell you by
        email before a subscription period ends. You can stop or change your
        plan at any time from your account page; stopping takes effect at the
        end of the period you have already paid for.
      </p>

      <h2>5. Right of withdrawal and refunds</h2>
      <p>
        If you are a consumer in the European Union or the United Kingdom, you
        normally have <strong>14 days</strong> to withdraw from a distance
        contract for digital services without giving a reason.
      </p>
      <p>
        Because Don Quack gives you immediate access to forecasts as soon as
        your subscription activates, by completing checkout you expressly
        request that we begin supplying the service during the withdrawal
        period, and you acknowledge that{' '}
        <strong>
          you lose your right of withdrawal once the service has been fully
          supplied
        </strong>
        . Where the service has only been partly supplied, you may withdraw and
        we will refund the proportion not yet supplied.
      </p>
      <p>
        Outside those circumstances, payments are non-refundable, except where a
        refund is required by law or where we have failed to supply the service.
        Refunds, where due, are made in cryptocurrency to a wallet address you
        provide, less any network fees. Requests go to{' '}
        <Fill>[support email]</Fill>.
      </p>
      <p>
        <Fill>
          [Confirm with counsel: the withdrawal-waiver wording must match the
          consumer-rights regime of your country of establishment.]
        </Fill>
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          scrape, crawl, bulk-download, or systematically extract forecasts or
          any other content from the service;
        </li>
        <li>
          resell, redistribute, sublicense, or publish our forecasts, whether
          free or for a fee, without our written permission;
        </li>
        <li>
          use the service to build or train a competing model or dataset;
        </li>
        <li>
          share one account between multiple people, or circumvent free-tier
          limits by creating multiple accounts;
        </li>
        <li>
          attempt to gain unauthorised access to the service, probe or test its
          security, or interfere with its normal operation;
        </li>
        <li>use the service for anything unlawful.</li>
      </ul>

      <h2>7. Intellectual property</h2>
      <p>
        The service, including its forecasts, text, design, logos, and software,
        belongs to us or our licensors and is protected by intellectual property
        law. Your subscription grants you a personal, non-exclusive,
        non-transferable, revocable licence to access and view the content for
        your own private, non-commercial use, and nothing more.
      </p>
      <p>
        Team names, league names, and similar marks belong to their respective
        owners. We are not affiliated with, endorsed by, or sponsored by any
        sports league, team, or bookmaker.
      </p>

      <h2>8. Availability</h2>
      <p>
        We aim to keep Don Quack available and accurate, but we provide it{' '}
        <strong>&ldquo;as is&rdquo; and &ldquo;as available&rdquo;</strong>. We
        do not warrant that it will be uninterrupted, error-free, or that any
        forecast will prove correct. We depend on third-party data feeds and may
        suspend or change the service, including individual features and sports
        coverage, at any time.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, we are not liable for any
        losses arising from your use of, or reliance on, the service — including
        in particular{' '}
        <strong>any gambling losses or other financial losses</strong>, lost
        profits, or lost opportunities — nor for any indirect or consequential
        loss.
      </p>
      <p>
        Where our liability cannot be excluded, it is limited to the amount you
        paid us in the twelve months before the event giving rise to the claim.
      </p>
      <p>
        Nothing in these Terms limits liability for death or personal injury
        caused by negligence, for fraud, or for anything else that cannot
        lawfully be limited. If you are a consumer, your statutory rights are
        unaffected.
      </p>

      <h2>10. Suspension and termination</h2>
      <p>
        You may stop using Don Quack and delete your account at any time. We may
        suspend or terminate your account if you breach these Terms, if we
        reasonably suspect fraud or abuse, or if we are required to by law.
        Where we terminate without cause, we will refund the unused portion of
        any paid subscription.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms. If a change materially affects your rights we
        will give you reasonable notice by email or in the app before it takes
        effect. Continuing to use the service after that means you accept the
        updated Terms.
      </p>

      <h2>12. Governing law and disputes</h2>
      <p>
        These Terms are governed by the law of <Fill>[Jurisdiction]</Fill>, and
        the courts of <Fill>[Jurisdiction]</Fill> have exclusive jurisdiction.
        If you are a consumer resident in the EU, you keep the protection of the
        mandatory laws of your country of residence and may bring proceedings
        there. The European Commission&rsquo;s online dispute resolution
        platform is available at{' '}
        <Fill>[ODR link if you are EU-established]</Fill>.
      </p>

      <h2>13. Contact</h2>
      <p>
        <Fill>[Legal entity name]</Fill>, <Fill>[Registered address]</Fill> —{' '}
        <Fill>[support email]</Fill>.
      </p>
      <p>
        See also our <Link className="link" to="/privacy">Privacy Policy</Link>.
      </p>
    </LegalPage>
  );
}
